import { SalesBlockState, SalespageContent } from 'server/types';
import { SalesBlockDefinition } from 'client/courses/components/page-builder/types';
import * as HeroBlock from './hero-block';
import * as AboutBlock from './about-block';
import * as CredentialsBlock from './credentials-block';
import * as ServicesBlock from './services-block';
import * as TestimonialsBlock from './testimonials-block';
import * as CTABlock from './cta-block';
import { RpxResponse, rpx } from 'client/lib/rpx-client';
import { baseUrl } from 'shared/urls';

export function generateInitialContent({
  userId,
  aiData,
  domain,
}: {
  userId?: string;
  aiData?: RpxResponse<typeof rpx.aiInterview.generateCalendarPage>;
  domain: string;
}): SalespageContent {
  let id = 0;
  const blockState = (d: SalesBlockDefinition, initialState?: any) => {
    const stateData = initialState || d.initialState;
    const state: SalesBlockState = {
      id: `${d.type}-${++id}`,
      type: d.type,
      state: stateData,
    };
    if (d.initialBlockState) {
      Object.assign(state, d.initialBlockState);
    }
    return state;
  };

  // Use full URL for CTA buttons for now because
  // /meet-direct URLs are not rendered correctly on custom cuach domains.
  // Use relative URLs when that is fixed.
  const ctaURL = `${baseUrl({ domain })}/meet-direct/${userId}`;
  const heroInitialState = {
    ...(aiData?.hero || HeroBlock.initialState),
    callToAction: {
      ...(aiData?.cta?.callToAction || HeroBlock.initialState.callToAction),
      url: ctaURL,
    },
  };

  const ctaInitialState = {
    ...(aiData?.cta || CTABlock.initialState),
    callToAction: {
      ...(aiData?.cta?.callToAction || CTABlock.initialState.callToAction),
      url: ctaURL,
    },
  };

  const servicesInitialState = {
    ...(aiData?.services || ServicesBlock.initialState),
    cards: (aiData?.services?.cards || ServicesBlock.initialState.cards).map((card) => ({
      ...card,
      callToAction: {
        text: card.callToAction.text,
        url: ctaURL,
      },
    })),
  };

  const blocks: SalesBlockState<unknown>[] = [
    // Hero Section
    blockState(HeroBlock, heroInitialState),

    // About Me Section
    blockState(AboutBlock, aiData?.about),

    // Credentials Section
    blockState(CredentialsBlock, aiData?.credentials),

    // Services Section
    blockState(ServicesBlock, servicesInitialState),

    // Testimonials Section
    blockState(TestimonialsBlock, aiData?.testimonials),

    // Call to Action Section
    blockState(CTABlock, ctaInitialState),
  ];

  return {
    blockIds: blocks.map((x) => x.id),
    blocks: blocks.reduce(
      (acc, x) => {
        acc[x.id] = x;
        return acc;
      },
      {} as Record<string, SalesBlockState>,
    ),
  };
}
